import * as THREE from './three'
import { createNode } from './createNode'
import { storage } from '../storage'

// Shared implementation; activated only by a room's explicit public flag.
export class Video360 {
  constructor(world) {
    this.world = world
    this.items = []
    this.active = null
    this.cameraPosition = new THREE.Vector3()
    this.localPosition = new THREE.Vector3()
    this.destroyed = false
  }

  async mount(root) {
    if (globalThis.env?.PUBLIC_VIDEO360_ENABLED !== 'true') return
    const markers = new Map()
    root.traverse(node => {
      if (node.name !== 'group' || !node.id?.endsWith('_360')) return
      const list = markers.get(node.id) || []
      list.push(node)
      markers.set(node.id, list)
    })
    if (!markers.size) return
    try {
      const token = storage.get('authToken')
      const response = await fetch('/api/video-360', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        cache: 'no-store',
      })
      if (!response.ok) throw new Error('configuration unavailable')
      const config = await response.json()
      if (this.destroyed) return
      for (const entry of config.videos) {
        const matches = markers.get(entry.marker)
        if (matches?.length !== 1) continue
        this.add(matches[0], entry)
      }
      if (this.items.length) this.createControls()
    } catch {
      // Do not print media URLs, signed queries or session tokens.
      console.error('[video360] unable to load room configuration')
    }
  }

  add(marker, entry) {
    const inverse = marker.matrixWorld.clone()
    if (Math.abs(inverse.determinant()) < 1e-10) return
    inverse.invert()
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.playsInline = true
    video.preload = 'none'
    video.loop = false
    video.src = entry.url
    const texture = new THREE.VideoTexture(video)
    texture.colorSpace = THREE.SRGBColorSpace
    const geometry = new THREE.SphereGeometry(entry.radius, 64, 32)
    // Reverse the horizontal geometry, preserving readable panorama orientation.
    geometry.scale(-1, 1, 1)
    const material = new THREE.MeshBasicMaterial({ map: texture, toneMapped: false, fog: false, transparent: true, depthTest: false, depthWrite: false })
    const mesh = new THREE.Mesh(geometry, material)
    // Cover the room while inside; keep runtime action prompts (999) visible.
    mesh.renderOrder = 998
    mesh.matrixAutoUpdate = false
    mesh.matrix.copy(marker.matrixWorld)
    mesh.visible = false
    this.world.stage.scene.add(mesh)
    const beaconGeometry = new THREE.SphereGeometry(0.22, 24, 16)
    const beaconMaterial = new THREE.MeshBasicMaterial({ color: 0x33ddff, toneMapped: false, fog: false })
    const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial)
    beacon.position.setFromMatrixPosition(marker.matrixWorld)
    this.world.stage.scene.add(beacon)
    const item = { entry, video, texture, geometry, material, mesh, inverse, beacon, beaconGeometry, beaconMaterial, busy: false, message: '' }
    const position = new THREE.Vector3(0, -0.5, -1).applyMatrix4(marker.matrixWorld)
    item.action = createNode('action', {
      label: 'Play / Pause video 360', distance: entry.radius * Math.max(...marker.scale.toArray().map(Math.abs)) + 2,
      duration: 0.5, position: position.toArray(),
      onTrigger: () => {
        if (this.active !== item || item.waitRelease) return
        item.waitRelease = true
        this.toggle()
      },
    })
    video.addEventListener('error', () => {
      item.message = 'Video non disponibile: controllare sorgente, formato e CORS.'
      this.refreshControls()
    })
    for (const event of ['playing', 'pause', 'ended']) video.addEventListener(event, () => this.refreshControls())
    this.items.push(item)
  }

  createControls() {
    this.panel = document.createElement('div')
    this.panel.setAttribute('aria-label', 'Video 360')
    this.panel.style.cssText = 'position:fixed;bottom:92px;left:50%;transform:translateX(-50%);z-index:100;display:none;max-width:90vw;padding:12px;background:#14202eee;color:white;border-radius:10px;font:14px sans-serif;text-align:center;pointer-events:auto'
    this.caption = document.createElement('div')
    this.caption.style.marginBottom = '8px'
    this.playButton = this.button('Avvia video 360', () => this.toggle())
    this.restartButton = this.button('Ricomincia', () => {
      if (this.active) { this.active.video.currentTime = 0; this.refreshControls() }
    })
    this.muteButton = this.button('Disattiva audio', () => {
      if (this.active) { this.active.video.muted = !this.active.video.muted; this.refreshControls() }
    })
    this.panel.append(this.caption, this.playButton, this.restartButton, this.muteButton)
    for (const event of ['pointerdown', 'pointerup', 'click']) this.panel.addEventListener(event, e => e.stopPropagation())
    document.body.appendChild(this.panel)
  }

  button(label, onClick) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = label
    button.style.cssText = 'margin:3px;padding:9px 12px;border:0;border-radius:6px;background:white;color:#14202e;cursor:pointer;font:inherit'
    button.addEventListener('click', onClick)
    return button
  }

  async toggle() {
    const item = this.active
    if (!item || item.busy) return
    if (!item.video.paused) { item.video.pause(); return }
    item.message = ''
    item.busy = true
    // Call play synchronously in the user's input handler; resume audio in parallel.
    const resume = this.world.audio.ctx.resume().catch(() => {})
    try {
      await Promise.all([item.video.play(), resume])
      if (this.destroyed || this.active !== item) item.video.pause()
    } catch {
      item.message = 'Riproduzione non avviata. Premi Avvia; se persiste, controllare video e permessi.'
    } finally {
      item.busy = false
      this.refreshControls()
    }
  }

  refreshControls() {
    if (!this.panel || !this.active) return
    const { video, entry, message } = this.active
    this.caption.textContent = message || entry.title
    this.playButton.textContent = video.paused ? 'Avvia video 360' : 'Pausa'
    this.muteButton.textContent = video.muted ? 'Attiva audio' : 'Disattiva audio'
  }

  update() {
    if (!this.items.length) return
    const xr = !!this.world.xr.session
    this.cameraPosition.setFromMatrixPosition((xr ? this.world.xr.camera : this.world.camera).matrixWorld)
    if (!this.world.actions.btnDown) for (const item of this.items) item.waitRelease = false
    const inside = this.items.find(item => this.localPosition.copy(this.cameraPosition).applyMatrix4(item.inverse).lengthSq() < item.entry.radius ** 2) || null
    if (inside !== this.active) {
      if (this.active) {
        this.active.video.pause()
        this.active.mesh.visible = false
        this.active.action.deactivate()
      }
      this.active = inside
      if (inside) { inside.mesh.visible = true; this.refreshControls() }
    }
    for (const item of this.items) item.beacon.visible = !inside
    if (this.active && xr && !this.active.action.mounted) this.active.action.activate({ world: this.world })
    if (this.active && !xr && this.active.action.mounted) this.active.action.deactivate()
    this.panel.style.display = this.active && !xr ? 'block' : 'none'
  }

  destroy() {
    this.destroyed = true
    for (const item of this.items) {
      item.action.deactivate()
      item.video.pause()
      item.video.removeAttribute('src')
      item.video.load()
      item.mesh.removeFromParent()
      item.texture.dispose()
      item.geometry.dispose()
      item.material.dispose()
      item.beacon.removeFromParent()
      item.beaconGeometry.dispose()
      item.beaconMaterial.dispose()
    }
    this.items = []
    this.active = null
    this.panel?.remove()
  }
}
